import React from 'react';

import { Token, UserRole } from '@fly/shared';
import { useTokensColumnsTanstack, useTokensFilters } from '@fly/shared-hooks';
import { FlyTextOverflow, Search, TanstackTable } from '@fly/shared-ui';
import { useGetCurrentUserProfileQuery, useGetTokensQuery } from '@fly/store';

export type TokensTableProps = {
  environmentName?: string;
} & Partial<React.ComponentProps<typeof TanstackTable<Token>>>;

export const TokensTable = ({
  environmentName,
  ...tableProps
}: TokensTableProps) => {
  const {
    data: tokens = [],
    isLoading: tokensLoading,
    isFetching: tokensFetching,
  } = useGetTokensQuery({
    environmentName,
  });
  const { data: profileData } = useGetCurrentUserProfileQuery();

  const isAdmin = profileData?.role === UserRole.ADMIN;

  const columns = useTokensColumnsTanstack(isAdmin, {
    FlyTextOverflow,
    Search,
  });

  const tokenFilterDescriptors = useTokensFilters(tokens);

  // RTK `isLoading` is false during background refetch when cached data exists.
  // Empty list + refetch (e.g. after generateToken invalidates tags) must keep
  // the table in loading/skeleton state so we do not flash hideTableFragmentIfEmpty
  // empty UI with no table root — that races automation and users see a flicker.
  // Profile is NOT gating — it only controls the revoke column (isAdmin); columns
  // update reactively when profile arrives. Gating on profileLoading blocked the
  // entire table for 10-13s when /users/me was slow (Store Dashboard latency).
  const isLoading = tokensLoading || (tokensFetching && tokens.length === 0);

  return (
    <TanstackTable
      layout='fixed'
      searchKey='description'
      columns={columns}
      data={tokens}
      filterDescriptors={tokenFilterDescriptors}
      stateType='search'
      loading={isLoading}
      dataProcessing='client'
      paginationType='offset'
      {...tableProps}
    />
  );
};
